/**
 * Aufnahme über das Mikrofon (MediaRecorder).
 *
 * Bewusst NICHT die Web Speech API: die gibt es auf Android in der App gar
 * nicht, sie bricht bei Sprechpausen ab und verschluckt Fachbegriffe. Hier
 * wird ein Audioschnipsel aufgenommen und danach einmal an Whisper geschickt
 * (siehe services/ai.service.ts, `transcribe`) – das erkennt Fachsprache
 * deutlich zuverlässiger und läuft überall gleich.
 */

export interface Recording {
  blob: Blob;
  seconds: number;
}

export interface Recorder {
  start(): Promise<void>;
  stop(): Promise<Recording>;
  cancel(): void;
  /** Aktueller Pegel 0–1, für die Anzeige. */
  level(): number;
  readonly active: boolean;
}

/** Format, das der Browser kann UND Whisper versteht. */
function pickMime(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const type of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
}

export function micSupported(): boolean {
  return typeof MediaRecorder !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
}

export function createRecorder(): Recorder {
  let stream: MediaStream | null = null;
  let rec: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let startedAt = 0;
  let analyser: AnalyserNode | null = null;
  let audioCtx: AudioContext | null = null;
  let buffer: Uint8Array | null = null;

  function teardown(): void {
    try { audioCtx?.close(); } catch { /* ignore */ }
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    rec = null;
    analyser = null;
    audioCtx = null;
  }

  return {
    get active() {
      return rec?.state === "recording";
    },

    async start() {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const mimeType = pickMime();
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      // Pegelanzeige: ohne sie sieht man nicht, ob das Mikrofon überhaupt
      // etwas hört – und merkt es erst am leeren Transkript.
      try {
        audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        buffer = new Uint8Array(analyser.frequencyBinCount);
        source.connect(analyser);
      } catch {
        analyser = null;
      }
      startedAt = Date.now();
      rec.start(1000);
    },

    stop() {
      return new Promise<Recording>((resolve, reject) => {
        if (!rec || rec.state === "inactive") {
          teardown();
          reject(new Error("Es lief keine Aufnahme."));
          return;
        }
        const type = rec.mimeType || "audio/webm";
        rec.onstop = () => {
          const seconds = Math.round((Date.now() - startedAt) / 1000);
          const blob = new Blob(chunks, { type });
          teardown();
          resolve({ blob, seconds });
        };
        rec.stop();
      });
    },

    cancel() {
      try { rec?.stop(); } catch { /* ignore */ }
      teardown();
    },

    level() {
      if (!analyser || !buffer) return 0;
      analyser.getByteTimeDomainData(buffer as Uint8Array<ArrayBuffer>);
      let peak = 0;
      for (let i = 0; i < buffer.length; i++) peak = Math.max(peak, Math.abs(buffer[i] - 128));
      return Math.min(1, peak / 90);
    },
  };
}
